import { LitElement, css, html, nothing, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-tooltip';
import '../../vessel-loader/src/vessel-loader';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { EditableLoadstarValues, LoadstarModel, LoadstarNotificationType, encodeLoadstarSource } from '../../../src/models/loadstar-model';
import { AlertType, VesselAlertsModel } from '../../../src/models/vessel-alerts-model';
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { VesselLoaderManager } from '../../vessel-loader/src/vessel-loader-manager';
import { getLastUpdatedTime } from '../../voyage-eta-change-review/src/helper';
import { recalcRoutes } from '../../../src/utils/route-utils';
import { roundToDigits } from '../../../src/utils/helpers';
import { VesselPositionModel } from '../../../src/models/vessel-position-model';
import {
    EDITABLE_FIELD_PRECISION,
    EditableLoadstarFormValues,
    EditableLoadstarValidationErrors,
    ReviewRow,
    getLoadstarMaxRollAngle,
    parseLoadstarDecimalValue,
    parseLoadstarIntegerValue,
    validateLoadstarEditingValues,
} from './loadstar-helper';

@customElement('loadstar-review-wrapper')
export class LoadstarReviewWrapper extends LitElement {
    @property({ type: Boolean }) shown = false;
    @property({ attribute: false }) private alert: LoadstarNotificationType | null = null;
    @property({ attribute: false }) private currentPlan: VoyagePlan | null = null;
    @property({ type: Boolean }) private isLoading = false;
    @property({ type: Boolean }) private isEditing = false;
    @property({ attribute: false }) private editingValues: EditableLoadstarFormValues = this._createEmptyEditingValues();
    @property({ attribute: false }) private savedValues: EditableLoadstarValues | null = null;
    @property({ attribute: false }) private validationErrors: EditableLoadstarValidationErrors = {};

    static styles = css`
        :host {
            font-family: MaerskText;
        }

        .modal-content {
            min-width: 980px;
            max-width: 100%;
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .header {
            position: relative;
            min-height: 48px;
            padding-top: 7px;
        }

        .title {
            font-size: 22px;
        }

        .description {
            margin: 0;
            padding-right: 290px;
            font-size: 14px;
            line-height: 20px;
            color: #3d3d3d;
            padding-top: 7px;
        }

        .timestamps {
            position: absolute;
            top: 0;
            right: -28px;
            min-width: 250px;
            font-size: 12px;
            line-height: 16px;
            color: #606060;
            text-align: left;
        }

        .timestamp-row {
            white-space: nowrap;
        }

        .timestamp-row + .timestamp-row {
            margin-top: 2px;
        }

        .timestamp-value {
            color: #14a44d;
        }

        .section-title {
            font-size: 14px;
            font-weight: 700;
            line-height: 20px;
            color: #141414;
            margin-top: 2px;
        }

        .table {
            border: 1px solid #d7d7d7;
            border-radius: 8px;
            overflow: hidden;
        }

        table {
            width: 100%;
            border-collapse: collapse;
        }

        th,
        td {
            padding: 12px 16px;
            font-size: 14px;
            line-height: 20px;
            text-align: left;
            border-bottom: 1px solid #ececec;
        }

        tr:last-child td {
            border-bottom: none;
        }

        th {
            background: #f7f7f7;
            color: #141414;
            font-weight: 700;
        }

        .row-label {
            width: 30%;
        }

        .header-cell {
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }

        .header-cell button,
        .updated-header-action {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            border: none;
            background: transparent;
            color: #606060;
            cursor: pointer;
        }

        .header-cell button:hover,
        .updated-header-action:hover {
            color: #141414;
        }

        .header-cell svg,
        .updated-header-action svg {
            width: 14px;
            height: 14px;
        }

        mc-tag {
            --mds_core_tag_small_label_padding-left: 6px;
            --mds_core_tag_small_label_padding-right: 6px;
        }

        .button-icon {
            display: inline-flex;
            align-items: center;
            margin-right: 6px;
        }

        .button-icon svg {
            width: 14px;
            height: 14px;
        }

        .updated-cell {
            min-width: 180px;
        }

        .updated-cell-content {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            white-space: nowrap;
        }

        .updated-cell-content.invalid {
            color: #b42318;
        }

        .updated-cell-edit {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }

        .edit-input {
            width: auto;
            min-width: 1ch;
            max-width: 5ch;
            display: inline-block;
            box-sizing: border-box;
            border: none;
            outline: none;
            padding: 0;
            font: inherit;
            color: #141414;
            background: #ffffff;
            box-shadow: none;
        }

        .edit-input:focus {
            outline: none;
            box-shadow: none;
        }

        .edit-input.invalid {
            color: #b42318;
        }

        .validation-text {
            font-size: 12px;
            line-height: 16px;
            color: #b42318;
            white-space: normal;
        }

        .edit-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-start;
        }

        .edit-actions-row {
            min-height: 34px;
            display: flex;
            justify-content: flex-start;
            padding-top: 8px;
        }

        .footer {
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            padding-top: 12px;
        }

        .footer mc-button::part(button) {
            min-width: 140px;
        }

        .tooltip-content {
            width: 12rem;
            font-size: 14px;
        }
    `;

    updated(changedProps: PropertyValues) {
        super.updated(changedProps);
        if (changedProps.has('shown') && this.shown) {
            this._loadReviewData();
        }
    }
    private _checkIfPositionOutdated(): boolean {
            const position = VesselPositionModel!.position;
            if (!position || !position.dataSources) return false;

            const outOfDateTime = 1000 * 60 * 30; // 30 minutes grace period
            let latestNonManualTimestamp: string | null = null;

            // Find the latest timestamp from all non-manual sources
            for (const [sourceName, sourceData] of Object.entries(position.dataSources)) {
                if (sourceName.toLowerCase() !== 'manual' && sourceData.timestamp) {
                    if (!latestNonManualTimestamp || sourceData.timestamp > latestNonManualTimestamp) {
                        latestNonManualTimestamp = sourceData.timestamp;
                    }
                }
            }

            // If we found a non-manual source, check if it's outdated
            if (latestNonManualTimestamp) {
                const positionTime = new Date(latestNonManualTimestamp).getTime();
                const isOutdated = positionTime + outOfDateTime < Date.now();
                return isOutdated;
            }

            // No non-manual sources found
            return true;
        }
    private async _loadReviewData() {
        if (!this.shown) {
            return;
        }

        this.isLoading = true;
        this.isEditing = false;
        this.alert = LoadstarModel?.getCurrentAlert() || null;

        if (!this.alert) {
            this.isLoading = false;
            this._close();
            return;
        }

        const matchingPlan = await LoadstarModel!.getLatestMatchingPlan(this.alert);

        if (!matchingPlan) {
            this.isLoading = false;
            this._close();
            VoyageDialogPopupManager.createDialog({
                headerText: 'No matching voyage plan found',
                bodyText: 'No voyage plan matches the departure and arrival ports from the current LoadStar update.',
                noCloseButton: true,
            });
            return;
        }

        this.currentPlan = matchingPlan;
        this.savedValues = this._getEditableValuesFromAlert(this.alert);
        this.editingValues = this._toEditingValues(this.savedValues);
        this.validationErrors = validateLoadstarEditingValues(this.editingValues, getLoadstarMaxRollAngle());
        this.isLoading = false;
    }

    private _createEmptyEditingValues(): EditableLoadstarFormValues {
        return {
            fore_draft: '',
            aft_draft: '',
            fluid_gm: '',
            solid_gm: '',
            live_reefers: '',
            max_roll_angle: '',
        };
    }

    private _getEditableValuesFromAlert(alert: LoadstarNotificationType): EditableLoadstarValues {
        return {
            fore_draft: alert.data.fore_draft,
            aft_draft: alert.data.aft_draft,
            fluid_gm: alert.data.fluid_gm,
            solid_gm: alert.data.solid_gm,
            live_reefers: alert.data.live_reefers,
            max_roll_angle: alert.data.max_roll_angle,
        };
    }

    private _toEditingValues(values: EditableLoadstarValues | null): EditableLoadstarFormValues {
        if (!values) {
            return this._createEmptyEditingValues();
        }

        return {
            fore_draft: this._formatEditableDecimal(values.fore_draft, EDITABLE_FIELD_PRECISION.fore_draft),
            aft_draft: this._formatEditableDecimal(values.aft_draft, EDITABLE_FIELD_PRECISION.aft_draft),
            fluid_gm: this._formatEditableDecimal(values.fluid_gm, EDITABLE_FIELD_PRECISION.fluid_gm),
            solid_gm: this._formatEditableDecimal(values.solid_gm, EDITABLE_FIELD_PRECISION.solid_gm),
            live_reefers: `${values.live_reefers}`,
            max_roll_angle: this._formatEditableDecimal(values.max_roll_angle, EDITABLE_FIELD_PRECISION.max_roll_angle),
        };
    }

    private _formatEditableDecimal(value: number, fractionDigits: number): string {
        return `${Number(value.toFixed(fractionDigits))}`;
    }

    private _parseEditingValues(): EditableLoadstarValues | null {
        const decimalFields: Array<keyof EditableLoadstarValues> = ['fore_draft', 'aft_draft', 'fluid_gm', 'solid_gm', 'max_roll_angle'];
        const parsedDecimals = decimalFields.map((field) => parseLoadstarDecimalValue(this.editingValues[field]));
        const liveReefers = parseLoadstarIntegerValue(this.editingValues.live_reefers);

        if (parsedDecimals.some((value) => value === null) || liveReefers === null) {
            return null;
        }

        return {
            fore_draft: parsedDecimals[0]!,
            aft_draft: parsedDecimals[1]!,
            fluid_gm: parsedDecimals[2]!,
            solid_gm: parsedDecimals[3]!,
            max_roll_angle: parsedDecimals[4]!,
            live_reefers: liveReefers,
        };
    }

    private _formatValue(value: number | null | undefined, unit = '', fractionDigits = 1): string {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '-';
        }

        if (!unit) {
            return `${value}`;
        }

        return `${value.toFixed(fractionDigits)} ${unit}`;
    }

    private _formatUpdatedValue(field: keyof EditableLoadstarValues, unit = ''): string {
        const value = this.editingValues[field];

        if (!value.trim()) {
            return '-';
        }

        return unit ? `${value} ${unit}` : value;
    }

    private _getRows(): ReviewRow[] {
        if (!this.currentPlan || !this.savedValues) {
            return [];
        }

        return [
            {
                label: 'Fore Draft',
                current: this._formatValue(this.currentPlan.foreDraft, 'm', 1),
                updated: this._formatUpdatedValue('fore_draft', 'm'),
                field: 'fore_draft',
                inputMode: 'decimal',
                unit: 'm',
                step: '0.01',
            },
            {
                label: 'Aft Draft',
                current: this._formatValue(this.currentPlan.aftDraft, 'm', 1),
                updated: this._formatUpdatedValue('aft_draft', 'm'),
                field: 'aft_draft',
                inputMode: 'decimal',
                unit: 'm',
                step: '0.01',
            },
            {
                label: 'Fluid GM',
                current: this._formatValue(this.currentPlan.gmt, 'm', 2),
                updated: this._formatUpdatedValue('fluid_gm', 'm'),
                field: 'fluid_gm',
                inputMode: 'decimal',
                unit: 'm',
                step: '0.01',
            },
            {
                label: 'Solid GM',
                current: this._formatValue(this.currentPlan.solidGm, 'm', 2),
                updated: this._formatUpdatedValue('solid_gm', 'm'),
                field: 'solid_gm',
                inputMode: 'decimal',
                unit: 'm',
                step: '0.01',
            },
            {
                label: 'Live Reefers',
                current: this._formatValue(this.currentPlan.numberOfReefers),
                updated: this._formatUpdatedValue('live_reefers'),
                field: 'live_reefers',
                inputMode: 'integer',
                unit: '',
                step: '1',
            },
            {
                label: 'Max Roll Angle',
                current: this._formatValue(this.currentPlan.rollAngle, '°', 1),
                updated: this._formatUpdatedValue('max_roll_angle', '°'),
                field: 'max_roll_angle',
                inputMode: 'decimal',
                unit: '°',
                step: '0.01',
            },
        ];
    }

    private _startEditing() {
        if (!this.savedValues) {
            return;
        }

        this.editingValues = this._toEditingValues(this.savedValues);
        this.validationErrors = validateLoadstarEditingValues(this.editingValues, getLoadstarMaxRollAngle());
        this.isEditing = true;
    }

    private _cancelEditing() {
        this.editingValues = this._toEditingValues(this.savedValues);
        this.validationErrors = validateLoadstarEditingValues(this.editingValues, getLoadstarMaxRollAngle());
        this.isEditing = false;
    }

    private _saveEditing() {
        const parsedValues = this._parseEditingValues();
        const validationErrors = validateLoadstarEditingValues(this.editingValues, getLoadstarMaxRollAngle());
        this.validationErrors = validationErrors;

        if (!parsedValues || Object.keys(validationErrors).length > 0) {
            return;
        }

        this.savedValues = parsedValues;
        this.editingValues = this._toEditingValues(parsedValues);
        this.validationErrors = {};
        this.isEditing = false;
    }

    private _handleEditInput(field: keyof EditableLoadstarValues, event: InputEvent) {
        const target = event.target as HTMLInputElement;
        const nextValue = field === 'live_reefers'
            ? target.value.replace(/[^\d]/g, '')
            : this._sanitizeDecimalInput(target.value);
        this.editingValues = {
            ...this.editingValues,
            [field]: nextValue,
        };
        this.validationErrors = validateLoadstarEditingValues({
            ...this.editingValues,
            [field]: nextValue,
        }, getLoadstarMaxRollAngle());
    }

    private _sanitizeDecimalInput(value: string): string {
        const normalized = value.replace(',', '.').replace(/[^0-9.]/g, '');
        const [wholePart = '', ...fractionParts] = normalized.split('.');
        const fractionPart = fractionParts.join('').slice(0, 2);

        if (normalized.startsWith('.')) {
            return fractionPart ? `0.${fractionPart}` : '0.';
        }

        if (fractionParts.length === 0) {
            return wholePart;
        }

        return `${wholePart}.${fractionPart}`;
    }

    private _isSaveDisabled(): boolean {
        return this._parseEditingValues() === null || Object.keys(this.validationErrors).length > 0;
    }

    private _isAcceptDisabled(): boolean {
        return this.isEditing || Object.keys(this.validationErrors).length > 0;
    }

    private _renderUpdatedCell(row: ReviewRow) {
        const errorMessage = this.validationErrors[row.field];

        if (!this.isEditing) {
            return html`
                <div class="updated-cell-edit">
                    <div class="updated-cell-content ${errorMessage ? 'invalid' : ''}">${row.updated}</div>
                    ${errorMessage ? html`<span class="validation-text">${errorMessage}</span>` : nothing}
                </div>
            `;
        }

        const isIntegerField = row.inputMode === 'integer';
        return html`
            <div class="updated-cell-edit">
                <div class="updated-cell-content">
                    <input
                        class="edit-input ${errorMessage ? 'invalid' : ''}"
                        .value=${this.editingValues[row.field]}
                        type=${isIntegerField ? 'number' : 'text'}
                        inputmode=${isIntegerField ? 'numeric' : 'decimal'}
                        step=${row.step || 'any'}
                        min="0"
                        @input=${(event: InputEvent) => this._handleEditInput(row.field, event)}
                    >
                    ${row.unit ? html`<span>${row.unit}</span>` : nothing}
                </div>
                ${errorMessage ? html`<span class="validation-text">${errorMessage}</span>` : nothing}
            </div>
        `;
    }

    private _close() {
        this.dispatchEvent(new CustomEvent('close', {
            bubbles: true,
            composed: true,
        }));
    }

    private async _handleDecline() {
        if (this.alert?.id) {
            LoadstarModel?.dismissCurrentReview(this.alert.id, "Rejected");
        }
        this._close();
    }

    private async _handleAccept() {
        if (!this.alert) {
            return;
        }

        const latestPlan = await LoadstarModel!.getLatestMatchingPlan(this.alert);
        if (!latestPlan) {
            VoyageDialogPopupManager.createDialog({
                headerText: 'No matching voyage plan found',
                bodyText: 'No voyage plan matches the departure and arrival ports from the current LoadStar update.',
                noCloseButton: true,
            });
            this._close();
            return;
        }

        try {
            VesselLoaderManager.createLoader({
                headerText: 'Updating stowage plan',
                showCancelButton: false,
            });

            //update plan logic 
            const updatedPlan = LoadstarModel!.createUpdatedPlan(latestPlan, this.alert, this.savedValues || undefined);
            const { foreDraft, aftDraft, gmt, solidGm, rollAngle, numberOfReefers } = updatedPlan;
            if (foreDraft) latestPlan.foreDraft = roundToDigits(foreDraft, 1);
            if (aftDraft) latestPlan.aftDraft = roundToDigits(aftDraft, 1);
            if (gmt) latestPlan.gmt = roundToDigits(gmt, 2);
            if (solidGm) latestPlan.solidGm = roundToDigits(solidGm, 2);
            if (rollAngle) latestPlan.rollAngle = roundToDigits(rollAngle, 1);
            if (numberOfReefers !== undefined) latestPlan.numberOfReefers = updatedPlan.numberOfReefers;
            latestPlan.passagePlan.userInput = {
                ...latestPlan.passagePlan.userInput,
                loadstarSourceMask: encodeLoadstarSource({
                    foreDraft: !!foreDraft,
                    aftDraft: !!aftDraft,
                    fluidGm: !!gmt,
                    solidGm: !!solidGm,
                    rollAngle: !!rollAngle,
                    reefers: numberOfReefers !== undefined,
                }),
            };
           
            await recalcRoutes(latestPlan, false);

            VesselAlertsModel!.addAlert((<any>window)['appenv'].vesselIMONumber, {
                title: 'Successfully updated',
                message: 'LoadStar stowage values were applied and the voyage plan was recalculated.',
                id: `loadstar-success-${this.alert.id}`,
                type: AlertType.SuccessAlert,
            });

            LoadstarModel!.dismissCurrentReview(this.alert.id, 'Accepted');
            this._close();
        } catch (error) {
            console.error('Failed to apply LoadStar changes:', error);
            VoyageDialogPopupManager.createDialog({
                headerText: 'Failed to recalculate voyage plan',
                bodyText: 'The LoadStar values could not be applied and recalculated. Please try again.',
                isError: true,
                noCloseButton: true,
            });
        } finally {
            VesselLoaderManager.removeLoader();
        }
    }

    render() {
        const rows = this._getRows();

        return html`
            <mc-modal
                hiddenheading
                ?open=${this.shown}
                height="auto"
                dimension="large"
                backdropcloseactiondisabled
                escapecloseactiondisabled
                entercloseactiondisabled
                hiddenclose
            >
                ${this.isLoading || !this.alert || !this.currentPlan || !this.savedValues
                    ? nothing
                    : html`
                        <div class="modal-content">
                            <div class="header">
                                <span class="title">Stowage Plan Updated - Review Changes</span>
                                <p class="description">
                                    Please review the updated values and confirm your decision. Upon acceptance, the voyage plan will be recalculated.
                                </p>
                                <div class="timestamps">
                                    <div class="timestamp-row">Last updated: <span class="timestamp-value">${getLastUpdatedTime(this.alert.updatedAt || this.alert.created_at)}</span></div>
                                    <div class="timestamp-row">LoadStar data last exported: <span class="timestamp-value">${getLastUpdatedTime(this.alert.data.saved_at)}</span></div>
                                </div>
                            </div>

                            <div class="section-title">Details</div>

                            <div class="table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>
                                                <span class="header-cell">
                                                    <span>Current Value from Plan</span>
                                                    <mc-tooltip position="bottom-center">
                                                        <button slot="trigger" aria-label="About current values from plan">
                                                            ${SvgIcons.customLightQuestionCircle}
                                                        </button>
                                                        <span class="tooltip-content">These values are used for optimization and safety calculations of the routes for this voyage.</span>
                                                    </mc-tooltip>
                                                </span>
                                            </th>
                                            <th>
                                                <span class="header-cell">
                                                    <span>Updated Value</span>
                                                    <mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>
                                                    <button
                                                        class="updated-header-action"
                                                        aria-label="Edit updated LoadStar values"
                                                        ?disabled=${this.isEditing}
                                                        @click=${this._startEditing}
                                                    >
                                                        ${SvgIcons.pencil}
                                                    </button>
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${rows.map((row) => html`
                                            <tr>
                                                <td class="row-label">${row.label}</td>
                                                <td>${row.current}</td>
                                                <td>${this._renderUpdatedCell(row)}</td>
                                            </tr>
                                        `)}
                                    </tbody>
                                </table>
                            </div>

                            <div class="edit-actions-row">
                                ${this.isEditing ? html`
                                    <span class="edit-actions">
                                        <mc-button appearance="neutral" variant="outlined" @click=${this._cancelEditing}>Cancel</mc-button>
                                        <mc-button appearance="secondary" variant="filled" ?disabled=${this._isSaveDisabled()} @click=${this._saveEditing}>Save</mc-button>
                                    </span>
                                ` : nothing}
                            </div>

                            <div class="footer">
                                <mc-button appearance="primary" variant="filled" ?disabled=${this.isEditing} @click=${this._handleDecline}>
                                    <span class="button-icon">${SvgIcons.circleSlash}</span>Decline
                                </mc-button>
                                <mc-button appearance="primary" variant="filled" icon="check" ?disabled=${this._isAcceptDisabled()} @click=${this._handleAccept}>Accept</mc-button>
                            </div>
                        </div>
                    `}
            </mc-modal>
        `;
    }
}
